import {
  AlertCircleIcon,
  ArrowLeft01Icon,
  Login03Icon,
  RefreshIcon,
  ShieldKeyIcon,
} from '@hugeicons/core-free-icons';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  ERROR_REASONS,
  isSignedOutCode,
} from '@/components/feedback/api-error';
import { Icon } from '@/components/primitives/icon';
import { Button, buttonVariants } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { announceDebounced } from '@/lib/announce';
import { cn } from '@/lib/utils';

export interface ErrorStateProps {
  /** "Could not load transactions." Always opens with "Could not". */
  title: string;
  /** The plain cause: "The server did not answer in time." */
  reason?: string;
  /** Machine code shown as a small mono chip: TIMEOUT. */
  code?: string;
  /** Shows Retry (primary). A returned promise shows "Retrying…" until it settles. */
  onRetry?: () => void;
  /** Shows Go back (ghost). */
  onBack?: () => void;
  /** 'region' (default) fills a panel or table; 'inline' is compact for tiles and strips. */
  size?: 'region' | 'inline';
  className?: string;
}

/**
 * A failed load with no data to show (§6.4): AlertCircleIcon in danger ink,
 * "Could not load <thing>.", the reason, the code chip, Retry and Go back.
 * Signed-out errors offer "Sign in again"; refused access shows the no-access
 * copy instead of Retry.
 */
function causeText(
  isForbidden: boolean,
  reason: string | undefined,
  code: string | undefined,
): string | undefined {
  if (isForbidden) return 'Ask the super admin for access if you need it.';
  if (reason !== undefined) return reason;
  return code ? ERROR_REASONS[code] : undefined;
}

export function ErrorState({
  title,
  reason,
  code,
  onRetry,
  onBack,
  size = 'region',
  className,
}: Readonly<ErrorStateProps>) {
  const [retrying, setRetrying] = useState(false);
  const isSignedOut = isSignedOutCode(code);
  const isForbidden = code === 'FORBIDDEN';

  const heading = isForbidden ? 'You do not have access to this.' : title;
  const cause = causeText(isForbidden, reason, code);

  useEffect(() => {
    announceDebounced(
      'error-state',
      cause ? `${heading} ${cause}` : heading,
      300,
      'assertive',
    );
  }, [heading, cause]);

  function retry() {
    if (!onRetry || retrying) return;
    const result: unknown = onRetry();
    if (result instanceof Promise) {
      setRetrying(true);
      result.finally(() => setRetrying(false)).catch(() => undefined);
    }
  }

  const inline = size === 'inline';

  return (
    <div
      data-slot="error-state"
      className={cn(
        'flex w-full flex-col items-center justify-center text-center',
        inline ? 'gap-2 p-4' : 'min-h-48 gap-3 p-6',
        className,
      )}
    >
      <Icon
        icon={isForbidden ? ShieldKeyIcon : AlertCircleIcon}
        size={inline ? 20 : 24}
        className={isForbidden ? 'text-muted-foreground' : 'text-danger-ink'}
      />
      <div className="flex max-w-md flex-col items-center gap-1">
        <p className="text-base font-semibold text-foreground">{heading}</p>
        {cause ? (
          <p className="text-sm text-muted-foreground">{cause}</p>
        ) : null}
        {code && !isForbidden ? (
          <span className="mt-1 inline-flex h-5 items-center rounded-sm border border-border-strong px-1.5 font-mono text-xs text-muted-foreground">
            {code}
          </span>
        ) : null}
      </div>
      <ErrorActions
        isSignedOut={isSignedOut}
        showRetry={Boolean(onRetry) && !isSignedOut && !isForbidden}
        retrying={retrying}
        onRetry={retry}
        onBack={onBack}
      />
    </div>
  );
}

interface ErrorActionsProps {
  isSignedOut: boolean;
  showRetry: boolean;
  retrying: boolean;
  onRetry: () => void;
  onBack?: () => void;
}

function ErrorActions({
  isSignedOut,
  showRetry,
  retrying,
  onRetry,
  onBack,
}: Readonly<ErrorActionsProps>) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      {isSignedOut ? (
        <Link
          to="/sign-in"
          search={{ reason: 'expired' }}
          className={buttonVariants({ size: 'sm' })}
        >
          <Icon icon={Login03Icon} size={14} />
          Sign in again
        </Link>
      ) : null}
      {showRetry ? (
        <Button
          size="sm"
          onClick={onRetry}
          aria-busy={retrying || undefined}
          disabled={retrying}
        >
          {retrying ? (
            <Spinner className="size-3.5" />
          ) : (
            <Icon icon={RefreshIcon} size={14} />
          )}
          {retrying ? 'Retrying…' : 'Retry'}
        </Button>
      ) : null}
      {onBack ? (
        <Button size="sm" variant="ghost" onClick={onBack}>
          <Icon icon={ArrowLeft01Icon} size={14} />
          Go back
        </Button>
      ) : null}
    </div>
  );
}
